import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

// Mui Component Imports
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material"

// MUI Icon Imports
import CabinIcon from "@mui/icons-material/Cabin"
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone"
import SaveIcon from "@mui/icons-material/Save"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import PhoneIcon from "@mui/icons-material/Phone"
import MessageIcon from "@mui/icons-material/Message"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"

// Styled Component Imports
import {
  DentalIcon,
  GroupInfoAdvisor,
  GroupInfoAdvisorLeft,
  GroupInfoAdvisorRight,
  GroupInfoContact,
  GroupInfoContactSectionOne,
  GroupInfoContactSectionOneLeft,
  GroupInfoContactSectionOneRight,
  GroupInfoContactSectionThree,
  GroupInfoContactSectionTwo,
  GroupInfoCsm,
  GroupInfoCsmLeft,
  GroupInfoCsmRight,
  GroupInfoDeactivate,
  GroupInfoDeactivateButton,
  GroupInfoHeader,
  GroupInfoHeaderIconActiveButton,
  GroupInfoHeaderIconWrapper,
  GroupInfoHeaderLeft,
  GroupInfoRegion,
  GroupInfoRegionSectionOne,
  GroupInfoRegionSectionOneLeft,
  GroupInfoRegionSectionTwo,
  GroupInfoRegionSectionTwoLeft,
  GroupInfoRegionSectionTwoRight,
  GroupInfoWrapper,
  StyledInputLabel,
  groupInfoIconStyle,
} from "./Atom"
import { getTokenLocal, makeFullName } from "../../../../helper/utils"
import GroupInfoConfirmation from "../../Modal/GroupInfoConfirmation"
import PermsWrapper from "../../Auth/PermsWrapper"
import { permissions } from "../../../../config/constants"
import UserActivationConfirmation from "../../Modal/UserActivationConfirmation"
import { useResponseHandle } from "../../../hooks"
import { useNavigate } from "react-router-dom"
import UserAPI from "../../../../api/user"
import GroupProfileAPI from "../../../../api/groupProfile"

function GroupInfo(props: any) {
  const { groupInfoHandler, assignPrimaryContact, assignAdvisor, assignCsm } =
    props

  const dispatch = useDispatch()
  const groupProfileAPI = new GroupProfileAPI()
  const token: any = getTokenLocal()
  const navigate = useNavigate()

  const rolesAndPermsAPI = new UserAPI()

  const usersData = useSelector((state: any) => state.users?.usersData)
  const groupInfo = useSelector((state: any) => {
    return state.groupProfile.groupInfo
  })
  const allGa = useSelector((state: any) => {
    return state.users.gaData
  })
  const allAdvisors = useSelector((state: any) => {
    return state.users.advisorsData
  })
  const allCsm = useSelector((state: any) => {
    return state.users.csmData
  })

  const [isPcEditActive, setIsPcEditActive] = useState<boolean>(false)
  const [isAdvisorEditActive, setIsAdvisorEditActive] = useState<boolean>(false)
  const [isCsmEditActive, setIsCsmEditActive] = useState<boolean>(false)

  const [gaValue, setGaValue] = useState<string>("")
  const [advisorValue, setAdvisorValue] = useState<string>("")
  const [csmValue, setCsmValue] = useState<string>("")

  const [selectedGa, setSelectedGa] = useState<any>({})
  const [selectedAdvisor, setSelectedAdvisor] = useState<any>({})
  const [selectedCsm, setSelectedCsm] = useState<any>({})

  const [type, setType] = useState<string>("")
  const [activationStatus, setActivationStatus] = useState<string>(
    groupInfo.group_status === "active" ? "deActivate" : "reActivate",
  )
  const [open, setOpen] = useState<boolean>(false)

  const [isActivationButtonDisabled, setisActivationButtonDisabled] =
    useState<boolean>(false)
  const [statusConfirmationDialogOpen, setStatusConfirmationDialogOpen] =
    useState<boolean>(false)
  const handlePcAssignment = () => {
    if (selectedGa?.uid) {
      if (selectedGa?.uid !== groupInfo?.primary_contact?.id) {
        setType("pc")
        setOpen(true)
      }
    }
    setIsPcEditActive(false)
  }

  const handleAdvisorAssignment = () => {
    if (selectedAdvisor?.uid) {
      if (selectedAdvisor?.uid !== groupInfo?.advisor_list?.[0]?.advisor_id) {
        setType("advisor")
        setOpen(true)
      }
    }

    setIsAdvisorEditActive(false)
  }

  const handleCsmAssignment = () => {
    if (selectedCsm?.uid) {
      if (selectedCsm?.uid !== groupInfo?.csm_list?.[0]?.csm_id) {
        setType("csm")
        setOpen(true)
      }
    }

    setIsCsmEditActive(false)
  }

  useEffect(() => {
    if (groupInfo?.group_status === "active") {
      setActivationStatus("deActivate")
    } else if (groupInfo?.group_status === "inactive") {
      setActivationStatus("reActivate")
    }
  }, [groupInfo.group_status])

  const handleActivationStatus = () => {
    setStatusConfirmationDialogOpen(true)
    setisActivationButtonDisabled(true)
  }

  const deactivateUser = async () => {
    const response = await groupProfileAPI.updateGroupStatus(
      token,
      "inactive",
      groupInfo?.group_sk,
    )
    const result = useResponseHandle({
      apiResponse: response,
      successMessage: true,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        setActivationStatus("reActivate")
        setisActivationButtonDisabled(false)
      }
      setisActivationButtonDisabled(false)
    } else {
      setisActivationButtonDisabled(false)
      navigate("/auth")
    }

    return result
  }

  const reactivateUser = async () => {
    const response = await groupProfileAPI.updateGroupStatus(
      token,
      "active",
      groupInfo?.group_sk,
    )
    const result = useResponseHandle({
      apiResponse: response,
      successMessage: true,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        setActivationStatus("deActivate")
        setisActivationButtonDisabled(false)
      }
      setisActivationButtonDisabled(false)
    } else {
      setisActivationButtonDisabled(false)
      navigate("/auth")
    }
    return result
  }

  return (
    <GroupInfoWrapper>
      {/* Group Info Header Section */}

      <GroupInfoConfirmation
        open={open}
        setOpen={setOpen}
        type={type}
        groupInfo={groupInfo}
        selectedGa={selectedGa}
        setSelectedGa={setSelectedGa}
        setGaValue={setGaValue}
        selectedAdvisor={selectedAdvisor}
        setSelectedAdvisor={setSelectedAdvisor}
        setAdvisorValue={setAdvisorValue}
        selectedCsm={selectedCsm}
        setSelectedCsm={setSelectedCsm}
        setCsmValue={setCsmValue}
        assignPrimaryContact={assignPrimaryContact}
        assignAdvisor={assignAdvisor}
        assignCsm={assignCsm}
        groupInfoHandler={groupInfoHandler}
      />

      <GroupInfoHeader>
        <GroupInfoHeaderLeft>
          <GroupInfoHeaderIconWrapper>
            <DentalIcon />
            <GroupInfoHeaderIconActiveButton
              variant="text"
              sx={{
                color:
                  activationStatus === "deActivate" ? "#42B2A0" : "#F6A623",
                backgroundColor:
                  activationStatus === "deActivate" ? "#E5F9F0" : "#FFF0E9",
                borderColor:
                  activationStatus === "deActivate" ? "#42B2A0" : "#F6A623",
              }}
            >
              {activationStatus === "deActivate" ? "Active" : "Inactive"}
            </GroupInfoHeaderIconActiveButton>
          </GroupInfoHeaderIconWrapper>

          <Typography
            fontWeight={600}
            color={"#414241"}
            fontSize={"14px"}
            sx={{ whiteSpace: "nowrap", marginLeft: "15px" }}
          >
            {groupInfo?.group_name}
          </Typography>
        </GroupInfoHeaderLeft>

        {/* <EditTwoToneIcon
          sx={{ color: "#707070", cursor: "pointer" }}
          fontSize="small"
        /> */}
      </GroupInfoHeader>

      {/* contact details */}

      {/* <GroupInfoContact>
        <GroupInfoContactSectionOne>
          <GroupInfoContactSectionOneLeft>
            <Typography color={"#707170"} fontSize={"12px"}>
              Primary Contact
            </Typography>
            {isPcEditActive ? (
              <FormControl>
                {!gaValue ? (
                  <StyledInputLabel disabled shrink={false} id="ga-label">
                    Select
                  </StyledInputLabel>
                ) : null}
                <Select
                  labelId="ga-label"
                  id="ga-select"
                  value={gaValue}
                  onChange={(e: any) => setGaValue(e?.target?.value)}
                  sx={{
                    width: "150px",
                    height: "35px",
                  }}
                  renderValue={(value: any) => (value ? value : "Select")}
                  // placeholder="Select"
                >
                  {allGa?.map((ga: any) => (
                    <MenuItem
                      onClick={() => setSelectedGa(ga)}
                      value={makeFullName(ga?.first_name, ga?.last_name)}
                    >
                      {makeFullName(ga?.first_name, ga?.last_name)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box>
                <Typography
                  color={"black"}
                  fontSize={"14px"}
                  sx={{ fontWeight: "bold" }}
                >
                  {groupInfo?.primary_contact?.primary_contact
                    ? groupInfo?.primary_contact?.primary_contact
                    : "-"}
                </Typography>
                <Typography
                  color={"##414241"}
                  fontSize={"12px"}
                  sx={{ textTransform: "capitalize", fontWeight: "bold" }}
                >
                  {groupInfo?.primary_contact?.primary_contact_role?.replace(
                    /_+/g,
                    " ",
                  )}
                </Typography>
              </Box>
            )}
          </GroupInfoContactSectionOneLeft>

          <GroupInfoContactSectionOneRight>
           
            <IconButton
            // onBlur={() => setIsAdvisorEditActive(false)}
            >
              {isPcEditActive ? (
                <SaveIcon
                  sx={{ color: "#3C77EF", cursor: "pointer" }}
                  fontSize="small"
                  onClick={() => {
                    handlePcAssignment()
                  }}
                />
              ) : !groupInfo?.primary_contact?.primary_contact ? (
                <AddCircleOutlineIcon
                  sx={{ color: "#707070", cursor: "pointer" }}
                  fontSize="small"
                  onClick={() => {
                    setGaValue(groupInfo?.primary_contact?.primary_contact)
                    setIsPcEditActive(true)
                  }}
                />
              ) : (
                <EditTwoToneIcon
                  sx={{ color: "#707070", cursor: "pointer" }}
                  fontSize="small"
                  onClick={() => {
                    setGaValue(groupInfo?.primary_contact?.primary_contact)
                    setIsPcEditActive(true)
                  }}
                />
              )}
            </IconButton>
          </GroupInfoContactSectionOneRight>
        </GroupInfoContactSectionOne>

        <GroupInfoContactSectionTwo>
          <Typography color={"#707170"} fontSize={"12px"}>
            Email ID
          </Typography>
          <Typography
            color={"#414241"}
            fontSize={"14px"}
            sx={{ fontWeight: "bold" }}
          >
            {groupInfo?.primary_contact?.email
              ? groupInfo?.primary_contact?.email
              : "-"}
          </Typography>
        </GroupInfoContactSectionTwo>

        <GroupInfoContactSectionThree>
          <Typography color={"#707170"} fontSize={"12px"}>
            Contact Number
          </Typography>
          <Typography
            color={"#414241"}
            fontSize={"14px"}
            sx={{ fontWeight: "bold" }}
          >
            {groupInfo?.primary_contact?.phone
              ? groupInfo?.primary_contact?.phone
              : "-"}
          </Typography>
        </GroupInfoContactSectionThree>
      </GroupInfoContact> */}

      <GroupInfoCsm>
        <GroupInfoCsmLeft>
          <Typography color={"#707170"} fontSize={"12px"}>
            Group Admins
          </Typography>

          <Box>
            <Typography
              color={"black"}
              fontSize={"14px"}
              sx={{ fontWeight: "bold" }}
            >
              <li style={{ listStyle: "none" }}>
                {groupInfo?.group_admin_count || 0}
              </li>
            </Typography>
          </Box>
        </GroupInfoCsmLeft>
      </GroupInfoCsm>

      <GroupInfoCsm>
        <GroupInfoCsmLeft>
          <Typography color={"#707170"} fontSize={"12px"}>
            Customer Success Manager
          </Typography>

          <Box>
            <Typography
              color={"black"}
              fontSize={"14px"}
              sx={{ fontWeight: "bold" }}
            >
              <li style={{ listStyle: "none" }}>{groupInfo?.csm_count || 0}</li>
            </Typography>
          </Box>
        </GroupInfoCsmLeft>
      </GroupInfoCsm>

      {/* Advisor Details */}

      <GroupInfoAdvisor>
        <GroupInfoAdvisorLeft>
          <Typography color={"#707170"} fontSize={"12px"}>
            Advisor
          </Typography>
          {/* {isAdvisorEditActive ? (
            <FormControl>
              {!advisorValue ? (
                <StyledInputLabel disabled shrink={false} id="advisor-label">
                  Select
                </StyledInputLabel>
              ) : null}
              <Select
                labelId="advisor-label"
                id="advisor-select"
                value={advisorValue}
                // label="Age"
                onChange={(e: any) => setAdvisorValue(e?.target?.value)}
                sx={{ width: "150px", height: "35px" }}
                renderValue={(value: any) => (value ? value : "Select")}
              >
                {allAdvisors?.map((advisor: any) => (
                  <MenuItem
                    onClick={() => setSelectedAdvisor(advisor)}
                    value={makeFullName(
                      advisor?.first_name,
                      advisor?.last_name,
                    )}
                  >
                    {makeFullName(advisor?.first_name, advisor?.last_name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : ( */}
          <Box>
            <Typography
              color={"black"}
              fontSize={"14px"}
              sx={{ fontWeight: "bold" }}
            >
              {groupInfo.advisor_count || 0}
              {/* {allAdvisors?.map((advisor: any, advisorIndex: any) => {
                return (
                  <li key={advisorIndex} style={{ listStyle: "none" }}>
                    {advisor?.first_name
                      ? advisor?.first_name + " " + advisor?.last_name
                      : "-"}
                  </li>
                )
              })} */}
            </Typography>
          </Box>
          {/* )} */}
        </GroupInfoAdvisorLeft>
      </GroupInfoAdvisor>
      {/* <GroupInfoAdvisorRight>
          <IconButton>
            {isAdvisorEditActive ? (
              <SaveIcon
                sx={{ color: "#3C77EF", cursor: "pointer" }}
                fontSize="small"
                onClick={() => {
                  handleAdvisorAssignment()
                }}
              />
            ) : !groupInfo?.advisor_list?.[0]?.name ? (
              <PermsWrapper
                perm={permissions.CUSTOMER_MANAGEMENT_ASSIGN_ADVISOR_TO_GROUP}
              >
                <AddCircleOutlineIcon
                  sx={{ color: "#707070", cursor: "pointer" }}
                  fontSize="small"
                  onClick={() => {
                    setAdvisorValue(groupInfo?.advisor_list?.[0]?.name)
                    setIsAdvisorEditActive(true)
                  }}
                />
              </PermsWrapper>
            ) : (
              <EditTwoToneIcon
                sx={{ color: "#707070", cursor: "pointer" }}
                fontSize="small"
                onClick={() => {
                  setAdvisorValue(groupInfo?.advisor_list?.[0]?.name)
                  setIsAdvisorEditActive(true)
                }}
              />
            )}
          </IconButton>
        </GroupInfoAdvisorRight> */}
      <GroupInfoAdvisor>
        <GroupInfoAdvisorLeft>
          <Typography color={"#707170"} fontSize={"12px"}>
            Office/Regional Manager
          </Typography>
          {/* {isOfficeManagerEditActive ? (
            <FormControl>
              {!officeManagerValue ? (
                <StyledInputLabel disabled shrink={false} id="oM-label">
                  Select
                </StyledInputLabel>
              ) : null}
              <Select
                labelId="oM-label"
                id="oM-select"
                value={officeManagerValue}
                // label="Age"
                onChange={(e: any) => setOfficeManagerValue(e?.target?.value)}
                sx={{ width: "150px", height: "35px" }}
                renderValue={(value: any) => (value ? value : "Select")}
              >
                {allOfficeManagers?.map((oM: any) => (
                  <MenuItem
                    onClick={() => setOfficeManagerValue(oM)}
                    value={makeFullName(oM?.first_name, oM?.last_name)}
                  >
                    {makeFullName(oM?.first_name, oM?.last_name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : ( */}
          <Box>
            <Typography
              color={"black"}
              fontSize={"14px"}
              sx={{ fontWeight: "bold" }}
            >
              {groupInfo?.office_manager_count || 0}
              {/* {groupInfo?.office_managers_list?.map((oM: any, oMIndex: any) => {
                return (
                  <li key={oMIndex} style={{ listStyle: "none" }}>
                    {oM?.name ? oM?.name : "-"}
                  </li>
                )
              })} */}
            </Typography>
          </Box>
          {/* )} */}
        </GroupInfoAdvisorLeft>

        {/* <GroupInfoAdvisorRight>
          <IconButton
          // onBlur={() => setIsAdvisorEditActive(false)}
          >
            {isAdvisorEditActive ? (
              <SaveIcon
                sx={{ color: "#3C77EF", cursor: "pointer" }}
                fontSize="small"
                onClick={() => {
                  handleAdvisorAssignment()
                }}
              />
            ) : !groupInfo?.advisor_list?.[0]?.name ? (
              <PermsWrapper
                perm={permissions.CUSTOMER_MANAGEMENT_ASSIGN_ADVISOR_TO_GROUP}
              >
                <AddCircleOutlineIcon
                  sx={{ color: "#707070", cursor: "pointer" }}
                  fontSize="small"
                  onClick={() => {
                    setAdvisorValue(groupInfo?.advisor_list?.[0]?.name)
                    setIsAdvisorEditActive(true)
                  }}
                />
              </PermsWrapper>
            ) : (
              <EditTwoToneIcon
                sx={{ color: "#707070", cursor: "pointer" }}
                fontSize="small"
                onClick={() => {
                  setAdvisorValue(groupInfo?.advisor_list?.[0]?.name)
                  setIsAdvisorEditActive(true)
                }}
              />
            )}
          </IconButton>
        </GroupInfoAdvisorRight> */}
      </GroupInfoAdvisor>
      {/* <GroupInfoAdvisor>
        <GroupInfoAdvisorLeft>
          <Typography color={"#707170"} fontSize={"12px"}>
            Providers
          </Typography>
          <Box>
            <Typography
              color={"black"}
              fontSize={"14px"}
              sx={{ fontWeight: "bold" }}
            >
              <li style={{ listStyle: "none" }}>
                {groupInfo?.provider_count || 0}
              </li>
            </Typography>
          </Box>
        </GroupInfoAdvisorLeft>
      </GroupInfoAdvisor> */}

      {/* regions details */}

      <GroupInfoRegion>
        <GroupInfoRegionSectionOne>
          {/* <GroupInfoRegionSectionOneLeft>
                <Typography
                  sx={{
                    color: "#707170",
                    fontSize: "12px",
                  }}
                >
                  Users
                </Typography>
                <Typography
                  sx={{
                    color: "#414241",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  10
                </Typography>
              </GroupInfoRegionSectionOneLeft> */}
          <GroupInfoRegionSectionTwoLeft>
            <Typography
              sx={{
                color: "#707170",
                fontSize: "12px",
              }}
            >
              Regions
            </Typography>
            <Typography
              sx={{
                color: "#414241",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {groupInfo?.regions_count}
            </Typography>
          </GroupInfoRegionSectionTwoLeft>
        </GroupInfoRegionSectionOne>

        <GroupInfoRegionSectionTwo>
          <GroupInfoRegionSectionOneLeft>
            <Typography
              sx={{
                color: "#707170",
                fontSize: "12px",
              }}
            >
              Offices
            </Typography>
            <Typography
              sx={{
                color: "#414241",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {groupInfo?.practices_count}
            </Typography>
          </GroupInfoRegionSectionOneLeft>
        </GroupInfoRegionSectionTwo>
      </GroupInfoRegion>

      <GroupInfoDeactivate>
        <GroupInfoDeactivateButton
          variant="text"
          onClick={handleActivationStatus}
          disabled={isActivationButtonDisabled}
        >
          {activationStatus === "reActivate" ? "Reactivate" : "Deactivate"}
        </GroupInfoDeactivateButton>
      </GroupInfoDeactivate>
      <UserActivationConfirmation
        open={statusConfirmationDialogOpen}
        setOpen={setStatusConfirmationDialogOpen}
        type={activationStatus}
        selectedUser={groupInfo}
        deactivateUser={deactivateUser}
        reactivateUser={reactivateUser}
        isGroup
        setIsActivationButtonDisabled={setisActivationButtonDisabled}
      />
    </GroupInfoWrapper>
  )
}
export default GroupInfo
